import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import Input from "../../../../../components/inputs/Input";
import PrimaryButton from "../../../../../components/buttons/PrimaryButton";
import { useVerifyDeviceBySponsor } from "../../../../../services/microservice/micelu/device/use-device";
import DropdownFilter from "../../../../../components/inputs/DropdownFilter";
import { IModel } from "../../../../../services/microservice/micelu/device/device-service.interface";
import { useCreateUsedContext } from "../provider/createUsed.provider";
import { useCreateContext } from "../../provider/create.provider";
import { useGetAgentsPlans } from "../../../../../services/microservice/agents/plans/use-agents-plans";
import { useVerifyDevicesAgents } from "../../../../../services/microservice/agents/devices/use-devices-agents";

const schemaValidation1 = yup.object({
  documentNumber: yup
    .string()
    .matches(/^\d+$/, { message: " Requerido" })
    .required(" Requerido"),
  imei1: yup
    .string()
    .length(15, " Debe tener 15 dígitos")
    .matches(/^\d+$/, { message: " Requerido" })
    .required(" Requerido"),
  plan: yup.mixed().required(" Requerido"),
});

const schemaValidation2 = yup.object({
  documentNumber: yup
    .string()
    .matches(/^\d+$/, { message: " Debe ser números" })
    .required(" Requerido"),
  imei1: yup
    .string()
    .length(15, " Debe tener 15 dígitos")
    .matches(/^\d+$/, { message: " Debe ser números" })
    .required(" Requerido"),
  imei2: yup
    .string()
    .transform((value, originalValue) =>
      originalValue.trim() === "" ? undefined : value,
    )
    .length(15, " Debe tener 15 dígitos")
    .matches(/^\d+$/, { message: " Debe ser números" })
    .notRequired(),
  plan: yup.mixed().required(" Requerido"),
});

const schemaValidation3 = yup.object({
  documentNumber: yup
    .string()
    .matches(/^\d+$/, { message: " Debe ser números" })
    .required(" Requerido"),
  imei1: yup
    .string()
    .length(15, " Debe tener 15 dígitos")
    .matches(/^\d+$/, { message: " Debe ser números" })
    .required(" Requerido"),
  imei2: yup
    .string()
    .transform((value, originalValue) =>
      originalValue.trim() === "" ? undefined : value,
    )
    .length(15, " Debe tener 15 dígitos")
    .matches(/^\d+$/, { message: " Debe ser números" })
    .notRequired(),
  imei3: yup
    .string()
    .transform((value, originalValue) =>
      originalValue.trim() === "" ? undefined : value,
    )
    .length(15, " Debe tener 15 dígitos")
    .matches(/^\d+$/, { message: " Debe ser números" })
    .notRequired(),
  imei4: yup
    .string()
    .transform((value, originalValue) =>
      originalValue.trim() === "" ? undefined : value,
    )
    .length(15, " Debe tener 15 dígitos")
    .matches(/^\d+$/, { message: " Debe ser números" })
    .notRequired(),
  plan: yup.mixed().required(" Requerido"),
});

const schemaValidation = [
  schemaValidation1,
  schemaValidation2,
  schemaValidation3,
  schemaValidation3,
];

const formFields: { name: string; label: string; required: boolean }[][] = [
  [{ name: "imei1", label: "IMEI Dispositivo 1", required: true }],
  [
    { name: "imei1", label: "IMEI Dispositivo 1", required: true },
    { name: "imei2", label: "IMEI Dispositivo 2", required: false },
  ],
  [
    { name: "imei1", label: "IMEI Dispositivo 1", required: true },
    { name: "imei2", label: "IMEI Dispositivo 2", required: false },
    { name: "imei3", label: "IMEI Dispositivo 3", required: false },
    { name: "imei4", label: "IMEI Dispositivo 4", required: false },
  ],
  [
    { name: "imei1", label: "IMEI Dispositivo 1", required: true },
    { name: "imei2", label: "IMEI Dispositivo 2", required: false },
    { name: "imei3", label: "IMEI Dispositivo 3", required: false },
    { name: "imei4", label: "IMEI Dispositivo 4", required: false },
  ],
];

const DeviceMiCeluComponent = () => {
  const [quantityImeis, setQuantityImeis] = useState<any[]>([{ id: 1 }]);
  const schema = useMemo(
    () => schemaValidation[quantityImeis.length - 1],
    [quantityImeis],
  );
  const methods = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
  });
  const {
    control,
    watch,
    handleSubmit,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
  } = methods;
  const [error, setError] = useState<string>("");
  const { data: Plans } = useGetAgentsPlans("ENEL");
  const {
    setStep,
    setInfoPlanDevice,
    handleInitialState: handleInitialStateUsed,
  } = useCreateUsedContext();
  const { setStep: setStepGeneral } = useCreateContext();
  const { mutateAsync: verifyDevices, isLoading } = useVerifyDevicesAgents();

  const onSubmit = async (values) => {
    const data = {
      planID: values.plan.id,
      documentNumber: values.documentNumber,
      listImeis: [],
    };
    Object.keys(values)
      .filter((value) => value.startsWith("imei") && values[value])
      .forEach((imei, index) => {
        if (values[imei]) {
          data.listImeis.push({
            label: `Dispositivo ${index + 1}`,
            imei: values[imei],
          });
        }
      });

    try {
      const res = await verifyDevices({
        listImei: data.listImeis,
        sponsorID: "ENEL",
      });
      if (res) {
        handleNextStep({
          ...values,
          ...data,
        });
      }
    } catch (error) {
      if (error.status === 400) {
        setError(error?.response?.data?.error?.message);
      } else {
        setError("Ocurrio un error inesperado");
      }
    }
  };

  const handlePreviousStep = () => {
    handleInitialStateUsed();
    setStepGeneral(3);
  };

  const handleNextStep = (val) => {
    setStep((prev) => prev + 1);
    setInfoPlanDevice(val);
  };

  useEffect(() => {
    if (watch("plan")) {
      let planArray = [];
      const plan: any = watch("plan");
      for (let i = 1; i <= plan?.policies_quota; i++) {
        planArray.push({ id: i });
      }
      for (let i = plan?.policies_quota + 1; i < 5; i++) {
        setValue(`imei${i}` as any, "");
      }
      clearErrors();
      setQuantityImeis(planArray);
    }
  }, [watch("plan")]);
  return (
    <section className="w-full">
      <h2 className="mb-5 text-center text-3xl font-bold">
        <span className="text-buttonBlack">
          Información plan y dispositivo
        </span>
      </h2>
      <div className="mb-10 w-full">
        <FormProvider {...methods}>
          <form>
            <div className="grid grid-cols-2 gap-10">
              <Controller
                name="documentNumber"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    classNameContainer="w-full mb-7"
                    classNameInput="w-full"
                    placeholder="Número de documento"
                    required
                    error={errors?.documentNumber?.message}
                  />
                )}
              />
              <Controller
                name="plan"
                control={control}
                render={({ field }) => (
                  <DropdownFilter
                    {...field}
                    options={Plans?.data?.plans || []}
                    placeholder="Plan"
                    label="nombre"
                    city={false}
                    required
                    error={errors?.plan?.message}
                  />
                )}
              />
              {formFields[quantityImeis.length - 1].map((field, index) => (
                <Controller
                  key={index}
                  name={field.name as any}
                  control={control}
                  render={({ field: imeiField }) => (
                    <Input
                      {...imeiField}
                      classNameContainer="w-full mb-7"
                      classNameInput="w-full"
                      placeholder={field.label}
                      required={field.required}
                      error={errors?.[field.name]?.message}
                      onChange={(e) => {
                        imeiField.onChange(e);
                        clearErrors(field.name as any);
                      }}
                    />
                  )}
                />
              ))}

              {error && <p className="text-md text-critical">{error}</p>}
            </div>
            <div className="flex w-full justify-between">
              <PrimaryButton
                title="Atras"
                onClick={() => handlePreviousStep()}
                className={
                  "text-textBlack mt-5 w-36 rounded-3xl border bg-transparent px-5 py-1 text-sm font-normal"
                }
              />
              <PrimaryButton
                title="Siguiente"
                onClick={handleSubmit(onSubmit)}
                className={
                  "bg-buttonBlack text-whiteSoft mt-5 w-36 rounded-3xl px-5 py-1 text-sm font-normal"
                }
                loading={isLoading}
              />
            </div>
          </form>
        </FormProvider>
      </div>
    </section>
  );
};

export default DeviceMiCeluComponent;
